import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import { enqueueSnackbar } from "notistack";
import {
  archivePartnerTaskOrder,
  findPartnerCustomerProfile,
  getPartnerCustomerByOrgcode,
  listPartnerCustomers,
  listPartnerTaskOrders,
} from "../../../helpers/partnerCentral_helper";
import TaskOrdersDialog from "../../PartnerCentral/TaskOrdersDialog";

const ADD_TASK_ORDER_PATH = "/partner-central/customer/add-task-order";
const FINOPS_RETURN_TO = "/customer-management";

const actionBtnSx = {
  color: "#64748b",
  padding: "4px",
  "&:hover": {
    backgroundColor: "#f1f5f9",
    color: "#334155",
  },
};

const asPartnerRecord = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  if (payload.id) return payload;
  if (Array.isArray(payload.items) && payload.items[0]?.id) return payload.items[0];
  if (payload.data?.id) return payload.data;
  return null;
};

/**
 * Resolve FinOps Customer Details row → Partner Central commercial profile.
 * Task Order APIs require the Partner Central profile id (unchanged).
 */
export const resolvePartnerCustomerForFinOps = async (finopsCustomer) => {
  const orgcode = String(finopsCustomer?.orgcode || "").trim();
  if (orgcode) {
    try {
      const byOrgcode = asPartnerRecord(await getPartnerCustomerByOrgcode(orgcode));
      if (byOrgcode?.id) return byOrgcode;
    } catch (_error) {
      // Fall through to name match.
    }
  }
  const byName = await findPartnerCustomerProfile({
    organizationName: finopsCustomer?.customer_name,
  });
  if (byName?.id) return byName;
  throw new Error(
    "No Partner Central customer profile found for this customer. Complete commercial customer onboarding first."
  );
};

const buildTaskOrderPath = ({ customerId, taskOrderId }) => {
  const params = new URLSearchParams();
  params.set("customerId", String(customerId));
  if (taskOrderId) params.set("id", String(taskOrderId));
  params.set("returnTo", FINOPS_RETURN_TO);
  return `${ADD_TASK_ORDER_PATH}?${params.toString()}`;
};

const organizationKeyForProfile = (profile) =>
  String(profile?.organization_id ?? profile?.id ?? "");

/**
 * Loads Partner Central profiles + task-order counts keyed by FinOps orgcode.
 * Same counting approach as Partner Central CustomersGrid.
 */
export const useFinOpsWorkloadCounts = (finopsCustomers) => {
  const [countsByOrgcode, setCountsByOrgcode] = useState({});
  const [profileByOrgcode, setProfileByOrgcode] = useState({});
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState(0);

  const customerKey = useMemo(
    () =>
      (finopsCustomers || [])
        .map((c) => String(c?.orgcode || "").trim())
        .filter(Boolean)
        .sort()
        .join("|"),
    [finopsCustomers]
  );

  useEffect(() => {
    if (!customerKey) {
      setCountsByOrgcode({});
      setProfileByOrgcode({});
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      listPartnerCustomers({ statuses: ["DRAFT", "ACTIVE"] }),
      listPartnerTaskOrders({ statuses: ["DRAFT", "ACTIVE"] }),
    ])
      .then(([profiles, orders]) => {
        if (cancelled) return;
        const profilesList = Array.isArray(profiles) ? profiles : [];
        const nextProfiles = {};
        profilesList.forEach((profile) => {
          const orgcode = String(profile?.customer_orgcode || "").trim();
          if (orgcode) nextProfiles[orgcode] = profile;
        });
        // Name fallback for FinOps rows missing orgcode match
        profilesList.forEach((profile) => {
          const name = String(profile?.organization_name || "").trim().toLowerCase();
          if (!name) return;
          if (!nextProfiles[`name:${name}`]) nextProfiles[`name:${name}`] = profile;
        });

        const orderCounts = {};
        (orders || []).forEach((order) => {
          const key = String(order.associated_organization_id ?? "");
          if (!key) return;
          orderCounts[key] = (orderCounts[key] || 0) + 1;
        });

        const nextCounts = {};
        Object.entries(nextProfiles).forEach(([mapKey, profile]) => {
          if (mapKey.startsWith("name:")) return;
          const orgKey = organizationKeyForProfile(profile);
          nextCounts[mapKey] = orderCounts[orgKey] || 0;
        });
        setProfileByOrgcode(nextProfiles);
        setCountsByOrgcode(nextCounts);
      })
      .catch(() => {
        if (!cancelled) {
          setCountsByOrgcode({});
          setProfileByOrgcode({});
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerKey, version]);

  const getCount = useCallback(
    (finopsCustomer) => {
      const orgcode = String(finopsCustomer?.orgcode || "").trim();
      if (orgcode && countsByOrgcode[orgcode] != null) return countsByOrgcode[orgcode];
      const name = String(finopsCustomer?.customer_name || "").trim().toLowerCase();
      const profile = name ? profileByOrgcode[`name:${name}`] : null;
      if (!profile) return 0;
      const orgKey = organizationKeyForProfile(profile);
      // Recount from cached profile org key via counts stored under orgcode of that profile
      const profileOrgcode = String(profile.customer_orgcode || "").trim();
      if (profileOrgcode && countsByOrgcode[profileOrgcode] != null) {
        return countsByOrgcode[profileOrgcode];
      }
      return 0;
    },
    [countsByOrgcode, profileByOrgcode]
  );

  const getCachedProfile = useCallback(
    (finopsCustomer) => {
      const orgcode = String(finopsCustomer?.orgcode || "").trim();
      if (orgcode && profileByOrgcode[orgcode]) return profileByOrgcode[orgcode];
      const name = String(finopsCustomer?.customer_name || "").trim().toLowerCase();
      return name ? profileByOrgcode[`name:${name}`] || null : null;
    },
    [profileByOrgcode]
  );

  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  return { getCount, getCachedProfile, loading, refresh };
};

/** AWS Accounts–style count badge for Workloads (click opens manage list even when 0). */
export const WorkloadCountBadge = ({ count, loading, onClick, disabled }) => {
  const empty = !count;
  const isBlocked = Boolean(disabled);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: isBlocked || loading ? "default" : "pointer",
        width: 32,
        height: 32,
        borderRadius: "50%",
        backgroundColor: empty ? "#f3f4f6" : "#f0f9ff",
        border: empty ? "1px solid #e2e8f0" : "1px solid #3b82f6",
        transition: "all 0.2s ease-in-out",
        opacity: isBlocked ? 0.6 : 1,
        ...(!isBlocked && !empty
          ? {
              "&:hover": {
                backgroundColor: "#dbeafe",
                borderColor: "#2563eb",
                boxShadow: "0 1px 3px rgba(59, 130, 246, 0.2)",
              },
            }
          : !isBlocked
            ? {
                "&:hover": {
                  backgroundColor: "#e2e8f0",
                  borderColor: "#cbd5e1",
                },
              }
            : {}),
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (!loading && !isBlocked) onClick?.(e);
      }}
      role="button"
      tabIndex={isBlocked ? -1 : 0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          if (!loading && !isBlocked) onClick?.(e);
        }
      }}
      aria-label={`${count} workload${count === 1 ? "" : "s"}`}
    >
      {loading ? (
        <CircularProgress size={14} sx={{ color: "#94a3b8" }} />
      ) : (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: empty ? "#9ca3af" : "#3b82f6",
            fontSize: "0.75rem",
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            lineHeight: 1,
          }}
        >
          {count}
        </Typography>
      )}
    </Box>
  );
};

WorkloadCountBadge.propTypes = {
  count: PropTypes.number,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
};

/**
 * Host for Workloads dialog + create action.
 * Count opens manage list; Actions only shows Create.
 */
const CustomerTaskOrderActions = ({
  customer,
  mode = "create",
  workloadCount = 0,
  countsLoading = false,
  getCachedProfile,
  onOrdersChanged,
}) => {
  const history = useHistory();
  const [resolving, setResolving] = useState(false);
  const [dialogCustomer, setDialogCustomer] = useState(null);
  const [dialogOrders, setDialogOrders] = useState([]);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [ordersVersion, setOrdersVersion] = useState(0);

  useEffect(() => {
    if (!dialogCustomer?.id) {
      setDialogOrders([]);
      setDialogError("");
      return undefined;
    }
    let cancelled = false;
    setDialogLoading(true);
    setDialogError("");
    listPartnerTaskOrders({
      associatedOrganizationId: dialogCustomer.organization_id ?? dialogCustomer.id,
      statuses: ["DRAFT", "ACTIVE"],
    })
      .then((items) => {
        if (!cancelled) setDialogOrders(items);
      })
      .catch((err) => {
        if (!cancelled) {
          setDialogOrders([]);
          setDialogError(err?.message || "Unable to load workorders.");
        }
      })
      .finally(() => {
        if (!cancelled) setDialogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dialogCustomer, ordersVersion]);

  const withPartnerCustomer = async (action) => {
    if (resolving || !customer) return;
    setResolving(true);
    try {
      const cached =
        typeof getCachedProfile === "function" ? getCachedProfile(customer) : null;
      const partnerCustomer = cached?.id
        ? cached
        : await resolvePartnerCustomerForFinOps(customer);
      await action(partnerCustomer);
    } catch (err) {
      enqueueSnackbar(err?.message || "Unable to open task orders for this customer.", {
        variant: "error",
      });
    } finally {
      setResolving(false);
    }
  };

  const openCreateTaskOrder = () => {
    withPartnerCustomer(async (partnerCustomer) => {
      history.push(buildTaskOrderPath({ customerId: partnerCustomer.id }));
    });
  };

  const openManageTaskOrders = () => {
    withPartnerCustomer(async (partnerCustomer) => {
      setDialogCustomer(partnerCustomer);
    });
  };

  const openEditTaskOrder = (order) => {
    if (!dialogCustomer?.id || !order?.id) return;
    const customerId = dialogCustomer.id;
    setDialogCustomer(null);
    history.push(buildTaskOrderPath({ customerId, taskOrderId: order.id }));
  };

  const handleDeleteTaskOrder = async (order) => {
    if (!order?.id || deletingId) return;
    const label = order.task_order_number || "this workorder";
    setDeletingId(order.id);
    try {
      await archivePartnerTaskOrder(order.id);
      enqueueSnackbar(`Workorder archived: ${label}.`, { variant: "success" });
      setOrdersVersion((value) => value + 1);
      if (typeof onOrdersChanged === "function") onOrdersChanged();
    } catch (err) {
      enqueueSnackbar(err?.message || "Unable to archive workorder.", { variant: "error" });
    } finally {
      setDeletingId(null);
    }
  };

  if (mode === "count") {
    return (
      <>
        <WorkloadCountBadge
          count={workloadCount}
          loading={countsLoading || resolving}
          onClick={openManageTaskOrders}
        />
        <TaskOrdersDialog
          open={Boolean(dialogCustomer)}
          onClose={() => setDialogCustomer(null)}
          customer={dialogCustomer}
          taskOrders={dialogOrders}
          loading={dialogLoading}
          error={dialogError}
          onEdit={openEditTaskOrder}
          onDelete={handleDeleteTaskOrder}
          deletingId={deletingId}
        />
      </>
    );
  }

  return (
    <Tooltip title="Create task order" arrow>
      <span>
        <IconButton
          size="small"
          aria-label={`Create task order for ${customer?.customer_name || "customer"}`}
          disabled={resolving}
          onClick={openCreateTaskOrder}
          sx={actionBtnSx}
        >
          {resolving ? (
            <CircularProgress size={16} sx={{ color: "#64748b" }} />
          ) : (
            <NoteAddOutlinedIcon sx={{ fontSize: "18px" }} />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};

CustomerTaskOrderActions.propTypes = {
  customer: PropTypes.shape({
    orgcode: PropTypes.string,
    customer_name: PropTypes.string,
  }),
  mode: PropTypes.oneOf(["create", "count"]),
  workloadCount: PropTypes.number,
  countsLoading: PropTypes.bool,
  getCachedProfile: PropTypes.func,
  onOrdersChanged: PropTypes.func,
};

export default CustomerTaskOrderActions;
